import { useMemo, useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useApi } from '../lib/useApi';
import { money, fmtDate } from '../lib/format';
import { Loader, Empty, Toast } from '../ui/common';
import CrmModal from '../clientModule/components/CrmModal';
import { IconEdit, IconTrash, IconPayment } from '../clientModule/components/SvgIcons';

const TABS = [
  { key: 'income', label: 'Income Records' },
  { key: 'expenses', label: 'Expense Records' },
  { key: 'balance', label: 'Account Balances' },
];

export default function Transactions({ config, onSaved }) {
  const [tab, setTab] = useState('income');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState('');
  const { data, loading, error, reload } = useApi(`/api/${tab}`);
  const currency = config.currency || '₹';
  const isBalance = tab === 'balance';
  const isExpense = tab === 'expenses';

  // Expense-specific filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    setDeleteBusy(true);
    try {
      await api.del(`/api/${tab}/${confirmDelete.id}`);
      setToast('Transaction deleted successfully');
      setConfirmDelete(null);
      reload();
      window.dispatchEvent(new Event('syncSheet'));
      onSaved && onSaved();
    } catch (e) {
      setToast(`Error: ${e.message}`);
    } finally {
      setDeleteBusy(false);
    }
  };

  // Vendors list
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    api.get('/api/vendors').then((d) => setVendors(d || [])).catch(() => {});
  }, []);

  const clearFilters = () => {
    setFilterCategory(''); setFilterVendor('');
    setFilterDateFrom(''); setFilterDateTo('');
    setFilterAmountMin(''); setFilterAmountMax('');
    setQ('');
  };

  const hasActiveFilters = filterCategory || filterVendor || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax;

  const rows = useMemo(() => {
    const list = (data || []).slice().reverse();
    const s = q.trim().toLowerCase();

    return list.filter((r) => {
      // Text search
      if (s && ![r.category, r.account, r.description, r.remarks, r.date].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))) {
        return false;
      }
      // Expense-specific filters
      if (isExpense) {
        if (filterCategory && r.category !== filterCategory) return false;
        if (filterVendor && r.remarks !== filterVendor) return false;
        if (filterDateFrom && r.date < filterDateFrom) return false;
        if (filterDateTo && r.date > filterDateTo) return false;
        const amt = r.net || r.amount || 0;
        if (filterAmountMin && amt < Number(filterAmountMin)) return false;
        if (filterAmountMax && amt > Number(filterAmountMax)) return false;
      }
      return true;
    });
  }, [data, q, isExpense, filterCategory, filterVendor, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax]);

  // Aggregates for filtered expense rows
  const totalFiltered = isExpense ? rows.reduce((s, r) => s + (r.net || r.amount || 0), 0) : 0;

  // Unique expense categories from actual data
  const uniqueCategories = useMemo(() => {
    if (!isExpense) return [];
    return [...new Set((data || []).map((r) => r.category).filter(Boolean))].sort();
  }, [data, isExpense]);

  // Unique vendors from actual data (remarks field)
  const uniqueVendors = useMemo(() => {
    if (!isExpense) return [];
    return [...new Set((data || []).map((r) => r.remarks).filter(Boolean))].sort();
  }, [data, isExpense]);

  return (
    <div>
      {toast && <Toast message={toast} onDone={() => setToast('')} />}

      {/* Segment Selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: tab === t.key ? 'none' : '1px solid #cbd5e1',
              background: tab === t.key
                ? t.key === 'income' ? '#224eae' : t.key === 'expenses' ? '#0f172a' : '#224eae'
                : '#ffffff',
              color: tab === t.key ? '#ffffff' : '#475569',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: tab === t.key ? '0 4px 14px rgba(0,0,0,0.15)' : 'none',
              transition: 'all 0.2s ease',
            }}
            onClick={() => { setTab(t.key); setQ(''); clearFilters(); }}
          >
            {tab === t.key ? '✓ ' : ''}{t.label}
          </button>
        ))}
      </div>

      {/* Search + Filters bar */}
      <div style={{ marginBottom: 16 }}>
        <div className="cm-ref-toolbar">
          <input
            className="cm-ref-search"
            placeholder={isExpense ? 'Search category, vendor, description…' : 'Search client, category, account…'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {isExpense && (
              <button
                type="button"
                style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid',
                  borderColor: hasActiveFilters ? '#224eae' : '#cbd5e1',
                  background: hasActiveFilters ? '#f0f4ff' : '#fff',
                  color: hasActiveFilters ? '#224eae' : '#475569',
                  fontWeight: 800, cursor: 'pointer', fontSize: '0.85rem',
                }}
                onClick={() => setShowFilters((p) => !p)}
              >
                🔍 Filters{hasActiveFilters ? ` (${[filterCategory, filterVendor, filterDateFrom, filterAmountMin].filter(Boolean).length} active)` : ''}
              </button>
            )}
            {hasActiveFilters && (
              <button
                type="button"
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontWeight: 800, cursor: 'pointer', fontSize: '0.8rem' }}
                onClick={clearFilters}
              >
                ✕ Clear
              </button>
            )}
            <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 700 }}>
              {rows.length} entries{isExpense && rows.length > 0 ? ` · Total: ${money(totalFiltered, currency)}` : ''}
            </div>
          </div>
        </div>

        {/* Expense Filter Panel */}
        {isExpense && showFilters && (
          <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px 20px', marginTop: 10 }}>
            <div style={{ fontWeight: 800, color: 'var(--ink)', marginBottom: 14, fontSize: '0.88rem' }}>🔍 Filter Expense Records</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>

              <div className="cm-field-group" style={{ margin: 0 }}>
                <label>Expense Category</label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value="">All Categories</option>
                  {uniqueCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="cm-field-group" style={{ margin: 0 }}>
                <label>Vendor / Paid To</label>
                <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)}>
                  <option value="">All Vendors</option>
                  {uniqueVendors.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              <div className="cm-field-group" style={{ margin: 0 }}>
                <label>Date From</label>
                <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
              </div>

              <div className="cm-field-group" style={{ margin: 0 }}>
                <label>Date To</label>
                <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
              </div>

              <div className="cm-field-group" style={{ margin: 0 }}>
                <label>Min Amount (₹)</label>
                <input type="number" min="0" value={filterAmountMin} onChange={(e) => setFilterAmountMin(e.target.value)} placeholder="0" />
              </div>

              <div className="cm-field-group" style={{ margin: 0 }}>
                <label>Max Amount (₹)</label>
                <input type="number" min="0" value={filterAmountMax} onChange={(e) => setFilterAmountMax(e.target.value)} placeholder="No limit" />
              </div>
            </div>

            {/* Active Filter Summary */}
            {hasActiveFilters && (
              <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 700 }}>Active filters:</span>
                {filterCategory && <span style={{ background: 'var(--chip-bg)', color: 'var(--brand)', padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>Category: {filterCategory}</span>}
                {filterVendor && <span style={{ background: 'var(--chip-bg)', color: 'var(--brand)', padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>Vendor: {filterVendor}</span>}
                {filterDateFrom && <span style={{ background: 'var(--chip-bg)', color: 'var(--brand)', padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>From: {filterDateFrom}</span>}
                {filterDateTo && <span style={{ background: 'var(--chip-bg)', color: 'var(--brand)', padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>To: {filterDateTo}</span>}
                {filterAmountMin && <span style={{ background: 'var(--bg)', color: 'var(--muted)', padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>Min: {money(Number(filterAmountMin), currency)}</span>}
                {filterAmountMax && <span style={{ background: 'var(--bg)', color: 'var(--muted)', padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>Max: {money(Number(filterAmountMax), currency)}</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <Empty>No financial transactions match your search filter.</Empty>
      ) : (
        <div className="cm-table-card">
          <table className="cm-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>{isBalance ? 'Account Name' : isExpense ? 'Expense Category' : 'Client / Category'}</th>
                {!isBalance && <th>Payment Method</th>}
                {isExpense && <th>Vendor / Paid To</th>}
                <th>Description</th>
                <th>Net Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r._row}>
                  <td style={{ fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                  <td style={{ fontWeight: 800, color: 'var(--ink)' }}>
                    {isBalance ? r.account : r.category}
                  </td>
                  {!isBalance && (
                    <td>
                      <span className="chip" style={{ background: 'var(--bg)', color: 'var(--muted)', fontWeight: 700 }}>
                        {r.account}
                      </span>
                    </td>
                  )}
                  {isExpense && (
                    <td>
                      {r.remarks ? (
                        <span className="chip" style={{ background: 'var(--bg)', color: 'var(--ink)', fontWeight: 700 }}>
                          {r.remarks}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                      )}
                    </td>
                  )}
                  <td style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                    {r.description || '—'}
                    {!isExpense && r.remarks && <span style={{ color: '#94a3b8' }}> ({r.remarks})</span>}
                  </td>
                  <td style={{
                    fontWeight: 800,
                    fontSize: '1rem',
                    color: isBalance ? (r.amount < 0 ? '#0f172a' : '#224eae') : tab === 'income' ? '#224eae' : '#0f172a'
                  }}>
                    {money(isBalance ? r.amount : r.net, currency)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      className="cm-action-icon-btn"
                      title="Edit Transaction"
                      onClick={() => setEditing(r)}
                    >
                      <IconEdit size={16} />
                    </button>
                    <button
                      className="cm-action-icon-btn"
                      title="Delete Transaction"
                      style={{ color: 'var(--ink)', marginLeft: 8 }}
                      onClick={() => setConfirmDelete(r)}
                    >
                      <IconTrash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditOverlay
          table={tab}
          record={editing}
          config={config}
          vendors={vendors}
          onClose={() => setEditing(null)}
          onDone={(msg) => {
            setEditing(null);
            setToast(msg);
            reload();
            window.dispatchEvent(new Event('syncSheet'));
            onSaved && onSaved();
          }}
        />
      )}

      {confirmDelete && (
        <CrmModal
          onClose={() => setConfirmDelete(null)}
          title="Confirm Delete"
          subtitle="This action cannot be undone."
          icon={IconTrash}
          maxWidth={450}
          footer={
            <>
              <button
                type="button"
                className="cm-btn-outline"
                onClick={() => setConfirmDelete(null)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cm-btn-emerald"
                style={{ background: '#0f172a', borderColor: '#0f172a', color: '#fff' }}
                onClick={handleDeleteConfirm}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </>
          }
        >
          <div style={{ padding: '10px 0', fontSize: '0.95rem', fontWeight: 600, color: 'var(--ink)' }}>
            Are you sure you want to delete this transaction record?
            <div style={{ marginTop: 12, background: 'var(--bg)', padding: 12, borderRadius: 8, border: '1px solid var(--line)' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: 4 }}>Details:</div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px 8px' }}>
                <span style={{ color: 'var(--muted)' }}>Date:</span>
                <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{fmtDate(confirmDelete.date)}</span>
                <span style={{ color: 'var(--muted)' }}>Category:</span>
                <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{confirmDelete.category || confirmDelete.account}</span>
                <span style={{ color: 'var(--muted)' }}>Amount:</span>
                <span style={{ color: 'var(--ink)', fontWeight: 800 }}>{money(confirmDelete.net || confirmDelete.amount, currency)}</span>
              </div>
            </div>
          </div>
        </CrmModal>
      )}
    </div>
  );
}

function EditOverlay({ table, record, config, vendors, onClose, onDone }) {
  const isBalance = table === 'balance';
  const isExpense = table === 'expenses';
  const [f, setF] = useState({
    date: record.date || '',
    category: record.category || '',
    account: record.account || '',
    amount: record.amount ?? '',
    description: record.description || '',
    remarks: record.remarks || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const cats = table === 'income' ? config.categories.income : config.categories.expense;

  const save = async () => {
    setBusy(true); setError(null);
    const payload = isBalance
      ? { date: f.date, account: f.account, amount: Number(f.amount), description: f.description, remarks: f.remarks }
      : { date: f.date, category: f.category, account: f.account, amount: Number(f.amount), tax: 0, shipping: 0, fees: 0, description: f.description, remarks: f.remarks };
    try { await api.put(`/api/${table}/${record.id}`, payload); onDone('Updated successfully'); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Are you sure you want to delete this transaction record?')) return;
    setBusy(true); setError(null);
    try { await api.del(`/api/${table}/${record.id}`); onDone('Deleted successfully'); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <CrmModal
      onClose={onClose}
      title="Edit Transaction Record"
      subtitle="Update sheet financial record or delete transaction"
      icon={IconPayment}
      maxWidth={600}
      footer={
        <>
          <button type="button" className="cm-btn-outline" style={{ color: 'var(--ink)', borderColor: 'var(--line)' }} disabled={busy} onClick={remove}>
            <IconTrash size={16} /> Delete Record
          </button>
          <button type="button" className="cm-btn-emerald" disabled={busy} onClick={save}>
            Save Changes
          </button>
        </>
      }
    >
      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="cm-form-section-card">
        <div className="cm-field-group">
          <label>Transaction Date *</label>
          <input type="date" value={f.date} onChange={set('date')} />
        </div>

        {!isBalance && (
          <div className="cm-field-group">
            <label>{isExpense ? 'Expense Category *' : 'Client / Category *'}</label>
            <select value={f.category} onChange={set('category')}>
              {!cats.includes(f.category) && f.category && <option value={f.category}>{f.category}</option>}
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        <div className="cm-field-group">
          <label>Bank / Cash Account *</label>
          <select value={f.account} onChange={set('account')}>
            {!config.accounts.some((a) => a.name === f.account) && f.account && <option value={f.account}>{f.account}</option>}
            {config.accounts.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
          </select>
        </div>

        <div className="cm-field-group">
          <label>Amount (₹) *</label>
          <input type="number" inputMode="decimal" value={f.amount} onChange={set('amount')} />
        </div>

        <div className="cm-field-group">
          <label>Description</label>
          <input value={f.description} onChange={set('description')} />
        </div>

        {/* Expense: Vendor / Paid To. Income: Remarks text */}
        <div className="cm-field-group">
          <label>{isExpense ? 'Vendor / Paid To' : 'Remarks'}</label>
          {isExpense ? (
            <select value={f.remarks} onChange={set('remarks')}>
              <option value="">— Select vendor —</option>
              {vendors.filter((v) => v.status !== 'Inactive').map((v) => (
                <option key={v.id} value={v.name}>{v.name}</option>
              ))}
              {/* Show current value even if vendor was deleted */}
              {f.remarks && !vendors.find((v) => v.name === f.remarks) && (
                <option value={f.remarks}>{f.remarks} (removed from vendor list)</option>
              )}
            </select>
          ) : (
            <input value={f.remarks} onChange={set('remarks')} placeholder="Optional note" />
          )}
        </div>
      </div>
    </CrmModal>
  );
}
